import type { ChatStatus } from "ai";
import {
	BadgeCheckIcon,
	CircleAlertIcon,
	TriangleAlertIcon,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import {
	ModelSelector,
	ModelSelectorContent,
	ModelSelectorEmpty,
	ModelSelectorGroup,
	ModelSelectorInput,
	ModelSelectorItem,
	ModelSelectorList,
	ModelSelectorTrigger,
} from "@/components/ai-elements/model-selector";
import {
	PromptInput,
	PromptInputBody,
	PromptInputFooter,
	PromptInputHeader,
	PromptInputProvider,
	PromptInputSubmit,
	PromptInputTextarea,
	PromptInputTools,
	usePromptInputController,
} from "@/components/ai-elements/prompt-input";
import { SpeechInput } from "@/components/ai-elements/speech-input";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogMedia,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { buildModelPickerSections } from "../../../../email-assistant/core/modelPicker";
import { CHAT_MODELS, type ChatModel, modelLabel } from "./chatModels";
import { readComposerDraft, writeComposerDraft } from "./composerDraftStorage";
import { useResourceMentions } from "./WorkflowMentionMenu";

function SpeechToText({
	onTranscription,
}: {
	onTranscription: (text: string) => void;
}) {
	return <SpeechInput onTranscriptionChange={onTranscription} />;
}

function SpeechAdapter() {
	const controller = usePromptInputController();
	return (
		<SpeechToText
			onTranscription={(text) => {
				const current = controller.textInput.value;
				const sep = current.length > 0 && !current.endsWith(" ") ? " " : "";
				controller.textInput.setInput(`${current}${sep}${text}`);
			}}
		/>
	);
}

function ComposerDraftPersistence({ scope }: { scope: string }) {
	const controller = usePromptInputController();
	const value = controller.textInput.value;
	const persistedRef = useRef(value);
	if (persistedRef.current !== value) {
		persistedRef.current = value;
		queueMicrotask(() => writeComposerDraft(scope, value));
	}
	return null;
}

function ComposerTextarea() {
	const { menuEl, onKeyDown } = useResourceMentions();

	return (
		<div className="relative w-full flex-1">
			{menuEl}
			<PromptInputTextarea
				data-testid="chat-composer-input"
				placeholder="Message Clerical…"
				onKeyDown={onKeyDown}
			/>
		</div>
	);
}

function ModelRecommendationBadge({ recommended }: { recommended: boolean }) {
	return (
		<Badge variant={recommended ? "secondary" : "outline"}>
			{recommended ? (
				<BadgeCheckIcon data-icon="inline-start" />
			) : (
				<CircleAlertIcon data-icon="inline-start" />
			)}
			{recommended ? "Recommended" : "Not recommended"}
		</Badge>
	);
}

export type ComposerProps = {
	draftScope: string;
	status: ChatStatus;
	model: string;
	recommendedModelIds: readonly string[] | undefined;
	onModelChange: (id: string) => void;
	onSubmit: (text: string) => void;
	onStop: () => void;
	disabled?: boolean;
};

export default function ChatComposer({
	draftScope,
	status,
	model,
	recommendedModelIds,
	onModelChange,
	onSubmit,
	onStop,
	disabled,
}: ComposerProps) {
	const [initialDraft] = useState(() => readComposerDraft(draftScope));
	const [modelOpen, setModelOpen] = useState(false);
	const [modelSearch, setModelSearch] = useState("");
	const [expandedProviders, setExpandedProviders] = useState<string[]>([]);
	const [pendingModel, setPendingModel] = useState<ChatModel | null>(null);
	const sections = useMemo(
		() =>
			buildModelPickerSections(
				CHAT_MODELS,
				recommendedModelIds ?? [],
				modelSearch,
				(candidate) => candidate.label,
			),
		[modelSearch, recommendedModelIds],
	);
	const providerValues = sections.providers.map(({ provider }) => provider);
	const hasMatches =
		sections.recommended.length > 0 || sections.providers.length > 0;

	const closeModelSelector = () => {
		setModelOpen(false);
		setModelSearch("");
		setExpandedProviders([]);
	};
	const selectModel = (selected: ChatModel, recommended: boolean) => {
		if (recommended) {
			onModelChange(selected.id);
			closeModelSelector();
			return;
		}
		setPendingModel(selected);
		closeModelSelector();
	};

	return (
		<PromptInputProvider initialInput={initialDraft}>
			<ComposerDraftPersistence scope={draftScope} />
			<PromptInput
				className="w-full [&>[data-slot=input-group]]:overflow-visible"
				accept=""
				maxFiles={4}
				maxFileSize={20 * 1024 * 1024}
				onSubmit={(message) => {
					const text = message.text?.trim() ?? "";
					if (!text || disabled) return;
					onSubmit(text);
				}}
			>
				<PromptInputHeader>
					<div className="flex w-full items-center gap-2 px-2 pt-1">
						<ModelSelector
							open={modelOpen}
							onOpenChange={(open) => {
								setModelOpen(open);
								if (!open) {
									setModelSearch("");
									setExpandedProviders([]);
								}
							}}
						>
							<ModelSelectorTrigger
								render={
									<Button
										type="button"
										data-testid="chat-model-selector"
										variant="outline"
										size="sm"
										className="h-7 gap-1.5 text-xs"
										disabled={disabled || recommendedModelIds === undefined}
									>
										{modelLabel(model)}
									</Button>
								}
							/>
							<ModelSelectorContent className="sm:max-w-lg">
								<ModelSelectorInput
									placeholder="Search models…"
									value={modelSearch}
									onValueChange={setModelSearch}
								/>
								<ModelSelectorList className="max-h-[min(70vh,28rem)]">
									{sections.recommended.length > 0 ? (
										<ModelSelectorGroup heading="Subscription models">
											{sections.recommended.map((recommendedModel) => (
												<ModelSelectorItem
													key={recommendedModel.id}
													data-model-id={recommendedModel.id}
													value={`${recommendedModel.provider} ${recommendedModel.label} ${recommendedModel.id}`}
													onSelect={() => selectModel(recommendedModel, true)}
												>
													<span className="min-w-0 flex-1 truncate">
														{recommendedModel.label}
													</span>
													<ModelRecommendationBadge recommended />
												</ModelSelectorItem>
											))}
										</ModelSelectorGroup>
									) : null}
									{sections.providers.length > 0 ? (
										<div className="px-2 pt-2 pb-1 text-xs font-medium text-muted-foreground">
											Other providers
										</div>
									) : null}
									<Accordion
										multiple
										value={modelSearch ? providerValues : expandedProviders}
										onValueChange={(value) => {
											if (!modelSearch) setExpandedProviders(value);
										}}
										className="px-1"
									>
										{sections.providers.map(({ provider, models }) => (
											<AccordionItem
												key={provider}
												value={provider}
												className="last:border-b-0"
											>
												<AccordionTrigger className="px-2 py-2 hover:no-underline">
													<span className="flex min-w-0 items-center gap-2">
														<span className="truncate">{provider}</span>
														<Badge variant="outline">{models.length}</Badge>
													</span>
												</AccordionTrigger>
												<AccordionContent className="pb-1">
													<ModelSelectorGroup>
														{models.map((providerModel) => (
															<ModelSelectorItem
																key={providerModel.id}
																data-model-id={providerModel.id}
																value={`${providerModel.provider} ${providerModel.label} ${providerModel.id}`}
																onSelect={() =>
																	selectModel(providerModel, false)
																}
															>
																<span className="min-w-0 flex-1 truncate">
																	{providerModel.label}
																</span>
																<ModelRecommendationBadge recommended={false} />
															</ModelSelectorItem>
														))}
													</ModelSelectorGroup>
												</AccordionContent>
											</AccordionItem>
										))}
									</Accordion>
									{!hasMatches ? (
										<ModelSelectorEmpty>No models match.</ModelSelectorEmpty>
									) : null}
								</ModelSelectorList>
							</ModelSelectorContent>
						</ModelSelector>
						<AlertDialog
							open={pendingModel !== null}
							onOpenChange={(open) => {
								if (!open) setPendingModel(null);
							}}
						>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogMedia>
										<TriangleAlertIcon />
									</AlertDialogMedia>
									<AlertDialogTitle>
										Select {pendingModel?.label}?
									</AlertDialogTitle>
									<AlertDialogDescription>
										This model costs more, Clerical recommends using one of the
										recommended models.
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel onClick={() => setPendingModel(null)}>
										Cancel
									</AlertDialogCancel>
									<AlertDialogAction
										onClick={() => {
											if (!pendingModel) return;
											onModelChange(pendingModel.id);
											setPendingModel(null);
										}}
									>
										Use {pendingModel?.label}
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</div>
				</PromptInputHeader>
				<PromptInputBody>
					<ComposerTextarea />
				</PromptInputBody>
				<PromptInputFooter
					data-testid="chat-stream-status"
					data-status={status}
				>
					<PromptInputTools>
						<SpeechAdapter />
					</PromptInputTools>
					<PromptInputSubmit
						data-testid="chat-submit"
						data-status={status}
						status={status}
						onStop={onStop}
						disabled={disabled}
					/>
				</PromptInputFooter>
			</PromptInput>
		</PromptInputProvider>
	);
}
